import * as React from "react"
import { Button } from "@/components/ui/button"

/**
 * Shown when rendering has failed, so it can't rely on the message catalog or
 * the intl provider (either may be what broke). Picks a language from <html lang>.
 */
const TEXT: Record<string, { title: string; body: string; reload: string }> = {
  en: {
    title: "Something went wrong",
    body: "The app hit an unexpected error. Reloading usually fixes it.",
    reload: "Reload",
  },
  es: {
    title: "Algo salió mal",
    body: "La aplicación tuvo un error inesperado. Recargar suele solucionarlo.",
    reload: "Recargar",
  },
  fr: {
    title: "Une erreur s’est produite",
    body: "L’application a rencontré une erreur inattendue. Un rechargement règle généralement le problème.",
    reload: "Recharger",
  },
}

function text() {
  const language = typeof document === "undefined" ? "en" : document.documentElement.lang.split("-")[0]
  return TEXT[language] ?? TEXT.en
}

interface ErrorBoundaryProps {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

/**
 * Top-level error boundary wrapping the Inertia <App>. Without it, an uncaught
 * render error leaves a dead, unscrollable shell — indistinguishable from a
 * frozen tab, with no browser chrome to reload from in a standalone PWA. The
 * "Reload" button gives the user a guaranteed way out.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Uncaught render error:", error, info)
  }

  render() {
    if (this.state.hasError) {
      const copy = text()
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-page px-6 text-center">
          <h1>{copy.title}</h1>
          <p>{copy.body}</p>
          <Button onClick={() => window.location.reload()}>{copy.reload}</Button>
        </div>
      )
    }

    return this.props.children
  }
}
